<template>
    <div class="seller-onboarding">
        <div v-if="loading" class="seller-onboarding__loading" role="status">
            <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
            Загружаем форму…
        </div>

        <template v-else>
            <nav class="seller-onboarding__progress" aria-label="Шаги начала работы">
                <button
                    v-for="(progressStep, index) in progressSteps"
                    :key="progressStep.key"
                    type="button"
                    :class="progressStepClass(progressStep.key)"
                    :disabled="index > currentStepIndex"
                    v-on:click="goToCompletedStep(progressStep.key, index)"
                >
                    <span>{{ index + 1 }}</span>
                    {{ progressStep.title }}
                </button>
            </nav>

            <div v-if="generalError" class="alert alert-danger" role="alert">
                {{ generalError }}
            </div>

            <form v-on:submit.prevent="submitCurrentStep">
                <section v-if="step === 'profile'">
                    <div class="seller-onboarding__heading">
                        <span>Шаг 1</span>
                        <h3>{{ state.authenticated ? 'Профиль готов' : 'Войди или создай профиль' }}</h3>
                        <p v-if="!state.authenticated">Всё происходит здесь — после входа форма продолжится с этого же места.</p>
                    </div>

                    <template v-if="state.authenticated">
                        <div class="seller-onboarding__current-profile">
                            <i class="bi bi-person-check"></i>
                            <div>
                                <strong>{{ state.user.name }}</strong>
                                <span>{{ state.user.email || 'Профиль СельхозМолла' }}</span>
                            </div>
                        </div>

                        <div class="seller-onboarding__actions">
                            <button type="submit" class="btn btn-success btn-lg" :disabled="sending">
                                {{ sending ? 'Подождите…' : 'Продолжить' }}
                            </button>
                        </div>
                    </template>

                    <template v-else>
                        <div class="seller-onboarding__tabs" role="tablist" aria-label="Вход или регистрация">
                            <button type="button" :class="{'active': profileMode === 'login'}" v-on:click="switchProfileMode('login')">
                                У меня есть профиль
                            </button>
                            <button type="button" :class="{'active': profileMode === 'register'}" v-on:click="switchProfileMode('register')">
                                Создать профиль
                            </button>
                        </div>

                        <div v-if="profileMode === 'register'" class="mb-3">
                            <label for="onboarding-name" class="form-label">Имя</label>
                            <input id="onboarding-name" v-model="profileForm.name" type="text" autocomplete="name" class="form-control form-control-lg" :class="{'is-invalid': errors.name}">
                            <field-errors :errors="errors.name" />
                        </div>

                        <div class="mb-3">
                            <label for="onboarding-email" class="form-label">Электронная почта</label>
                            <input id="onboarding-email" v-model="profileForm.email" type="email" autocomplete="email" class="form-control form-control-lg" :class="{'is-invalid': errors.email}">
                            <field-errors :errors="errors.email" />
                        </div>

                        <div class="mb-3">
                            <label for="onboarding-password" class="form-label">Пароль</label>
                            <input id="onboarding-password" v-model="profileForm.password" type="password" :autocomplete="profileMode === 'login' ? 'current-password' : 'new-password'" class="form-control form-control-lg" :class="{'is-invalid': errors.password}">
                            <field-errors :errors="errors.password" />
                        </div>

                        <div v-if="profileMode === 'register'" class="mb-3">
                            <label for="onboarding-password-confirmation" class="form-label">Повтори пароль</label>
                            <input id="onboarding-password-confirmation" v-model="profileForm.password_confirmation" type="password" autocomplete="new-password" class="form-control form-control-lg">
                        </div>

                        <p class="small text-secondary mt-4 mb-0">
                            Продолжая, ты соглашаешься с
                            <a :href="config.platformRulesUrl" target="_blank" rel="noopener">правилами Платформы</a>
                            и можешь ознакомиться с
                            <a :href="config.platformPrivacyPolicyUrl" target="_blank" rel="noopener">политикой обработки персональных данных</a>.
                        </p>

                        <div class="seller-onboarding__actions">
                            <button type="submit" class="btn btn-success btn-lg" :disabled="sending">
                                {{ sending ? 'Подождите…' : (profileMode === 'login' ? 'Войти и продолжить' : 'Создать профиль') }}
                            </button>
                        </div>
                    </template>
                </section>

                <section v-else-if="step === 'account'">
                    <div class="seller-onboarding__heading">
                        <span>Шаг 2</span>
                        <h3>Бизнес-аккаунт</h3>
                        <p>Он объединяет реквизиты, сотрудников, магазины, отделы продаж и службы доставки одного бизнеса.</p>
                    </div>

                    <template v-if="state.accounts.length && !createNewAccount">
                        <div class="seller-onboarding__account-list">
                            <label v-for="account in state.accounts" :key="account.id" :class="{'selected': selectedAccountId === account.id}">
                                <input v-model="selectedAccountId" type="radio" :value="account.id" v-on:change="prepareSelectedAccount">
                                <span>
                                    <strong>{{ account.name }}</strong>
                                    <small>ИНН {{ account.inn }}</small>
                                </span>
                                <i class="bi bi-check-circle-fill"></i>
                            </label>
                        </div>
                        <field-errors :errors="errors.account_id" />
                        <button type="button" class="btn btn-outline-secondary mt-3" v-on:click="startNewAccount">
                            Создать другой бизнес-аккаунт
                        </button>
                    </template>

                    <template v-else>
                        <div class="row g-3">
                            <div class="col-12">
                                <label for="onboarding-account-name" class="form-label">Название бизнес-аккаунта</label>
                                <input id="onboarding-account-name" v-model="accountForm.name" class="form-control form-control-lg" :class="{'is-invalid': errors['account.name']}">
                                <field-errors :errors="errors['account.name']" />
                            </div>
                            <div class="col-md-6">
                                <label for="onboarding-account-legal-form" class="form-label">Форма деятельности</label>
                                <select id="onboarding-account-legal-form" v-model="accountForm.legal_form" class="form-select form-select-lg">
                                    <option value="ООО">ООО</option>
                                    <option value="ИП">ИП</option>
                                    <option value="Глава КФХ">Глава КФХ</option>
                                    <option value="Другая">Другая</option>
                                </select>
                            </div>
                            <div class="col-md-6">
                                <label for="onboarding-account-inn" class="form-label">ИНН</label>
                                <input id="onboarding-account-inn" v-model="accountForm.inn" inputmode="numeric" class="form-control form-control-lg" :class="{'is-invalid': errors['account.inn']}">
                                <field-errors :errors="errors['account.inn']" />
                            </div>
                            <div class="col-md-6">
                                <label for="onboarding-account-phone" class="form-label">Телефон</label>
                                <input id="onboarding-account-phone" v-model="accountForm.phone" type="tel" autocomplete="tel" class="form-control form-control-lg" :class="{'is-invalid': errors['account.phone']}" placeholder="+7">
                                <field-errors :errors="errors['account.phone']" />
                            </div>
                            <div class="col-md-6">
                                <label for="onboarding-account-email" class="form-label">Электронная почта</label>
                                <input id="onboarding-account-email" v-model="accountForm.email" type="email" autocomplete="email" class="form-control form-control-lg" :class="{'is-invalid': errors['account.email']}">
                                <field-errors :errors="errors['account.email']" />
                            </div>
                        </div>
                        <button v-if="state.accounts.length" type="button" class="btn btn-link px-0 mt-3" v-on:click="cancelNewAccount">
                            Выбрать существующий аккаунт
                        </button>
                    </template>

                    <div class="seller-onboarding__actions seller-onboarding__actions--between">
                        <button type="button" class="btn btn-link" v-on:click="step = 'profile'">Назад</button>
                        <button type="submit" class="btn btn-success btn-lg" :disabled="sending || (!createNewAccount && !selectedAccountId)">
                            Продолжить
                        </button>
                    </div>
                </section>

                <section v-else-if="step === 'modules'">
                    <div class="seller-onboarding__heading">
                        <span>Шаг 3</span>
                        <h3>Модули</h3>
                        <p>Выбери всё, что нужно для начала работы. Ни один модуль не включён заранее.</p>
                    </div>

                    <div class="seller-onboarding__capabilities">
                        <div class="seller-onboarding__module-card" :class="{'selected': moduleShop}">
                            <label class="seller-onboarding__module-toggle">
                                <input v-model="moduleShop" type="checkbox">
                                <i class="bi bi-shop"></i>
                                <span>
                                    <strong>Магазин на СельхозМолле</strong>
                                    <small>Товары, цены, доступное количество, даты продаж и заказы.</small>
                                    <em class="text-secondary">Для продаж на маркетплейсе</em>
                                </span>
                                <span class="seller-onboarding__module-check"><i class="bi bi-check-lg"></i></span>
                            </label>

                            <div v-if="moduleShop" class="seller-onboarding__module-fields">
                                <template v-if="selectedAccount && selectedAccount.shops.length && !createNewShop">
                                    <label for="onboarding-shop-select" class="form-label">Магазин</label>
                                    <select id="onboarding-shop-select" v-model="selectedShopId" class="form-select form-select-lg" :class="{'is-invalid': errors.shop_id}">
                                        <option v-for="shop in selectedAccount.shops" :key="shop.id" :value="shop.id">
                                            {{ shop.name }}{{ shop.is_active ? '' : ' — ожидает включения' }}
                                        </option>
                                    </select>
                                    <button v-if="canCreateShop" type="button" class="btn btn-link px-0 mt-2" v-on:click="startNewShop">
                                        Создать новый магазин
                                    </button>
                                </template>

                                <template v-else>
                                    <div v-if="canCreateShop">
                                        <label for="onboarding-shop-name" class="form-label">Название магазина</label>
                                        <input id="onboarding-shop-name" v-model="shopForm.name" class="form-control form-control-lg" :class="{'is-invalid': errors['shop.name']}" placeholder="Например, Ферма Лесная">
                                        <field-errors :errors="errors['shop.name']" />
                                    </div>
                                    <div v-else class="alert alert-warning mb-0">
                                        В этом бизнес-аккаунте нельзя создать ещё один магазин. Выбери существующий или напиши на office@fantum.ru.
                                    </div>
                                    <button v-if="selectedAccount?.shops.length" type="button" class="btn btn-link px-0 mt-2" v-on:click="chooseExistingShop">
                                        Выбрать существующий магазин
                                    </button>
                                </template>
                                <field-errors :errors="errors.shop_id || errors.service_limit" />
                            </div>
                        </div>
                        <div class="seller-onboarding__module-card" :class="{'selected': moduleSeller}">
                            <label class="seller-onboarding__module-toggle">
                                <input v-model="moduleSeller" type="checkbox">
                                <i class="bi bi-headset"></i>
                                <span>
                                    <strong>Собственный отдел продаж</strong>
                                    <small>Для работы с клиентской базой, заказами и командой продаж.</small>
                                    <em :class="capabilityStatusClass('seller')">{{ capabilityStatus('seller') }}</em>
                                </span>
                                <span class="seller-onboarding__module-check"><i class="bi bi-check-lg"></i></span>
                            </label>
                        </div>
                        <div class="seller-onboarding__module-card" :class="{'selected': moduleDelivery}">
                            <label class="seller-onboarding__module-toggle">
                                <input v-model="moduleDelivery" type="checkbox">
                                <i class="bi bi-truck"></i>
                                <span>
                                    <strong>Служба доставки</strong>
                                    <small>Курьеры, зоны и совместные маршруты с другими магазинами.</small>
                                    <em :class="capabilityStatusClass('delivery_service')">{{ capabilityStatus('delivery_service') }}</em>
                                </span>
                                <span class="seller-onboarding__module-check"><i class="bi bi-check-lg"></i></span>
                            </label>
                        </div>
                    </div>
                    <field-errors :errors="errors.modules" />

                    <div class="seller-onboarding__actions seller-onboarding__actions--between">
                        <button type="button" class="btn btn-link" v-on:click="step = 'account'">Назад</button>
                        <button type="submit" class="btn btn-success btn-lg" :disabled="sending">
                            {{ sending ? 'Сохраняем…' : 'Завершить настройку' }}
                        </button>
                    </div>
                </section>

                <div v-else-if="step === 'ready'" class="seller-onboarding__ready" role="status">
                    <div class="seller-onboarding__ready-icon"><i class="bi bi-check2"></i></div>
                    <div class="seller-onboarding__heading">
                        <span>Готово</span>
                        <h3>Начало положено</h3>
                        <p>Выбранные модули и необходимые запросы сохранены в бизнес-аккаунте.</p>
                    </div>

                    <div class="seller-onboarding__summary">
                        <div>
                            <i class="bi bi-building-check"></i>
                            <span><strong>{{ selectedAccount.name }}</strong>Бизнес-аккаунт готов.</span>
                        </div>
                        <div v-if="moduleShop && selectedShop">
                            <i class="bi bi-shop"></i>
                            <span>
                                <strong>{{ selectedShop.name }}</strong>
                                {{ selectedShop.is_active ? 'Магазин доступен для настройки.' : 'Магазин создан: его уже можно заполнять, а публикация ожидает включения администрацией.' }}
                            </span>
                        </div>
                        <div v-if="moduleSeller">
                            <i class="bi bi-headset"></i>
                            <span><strong>Собственный отдел продаж</strong>{{ capabilityCompletionText('seller') }}</span>
                        </div>
                        <div v-if="moduleDelivery">
                            <i class="bi bi-truck"></i>
                            <span><strong>Служба доставки</strong>{{ capabilityCompletionText('delivery_service') }}</span>
                        </div>
                    </div>

                    <div class="seller-onboarding__actions">
                        <a
                            v-if="moduleShop && selectedShop"
                            :href="'/shop/' + selectedShop.id"
                            class="btn btn-success btn-lg"
                        >Настроить магазин</a>
                        <a
                            v-if="moduleSeller && selectedSeller"
                            :href="'/seller/' + selectedSeller.id"
                            class="btn btn-success btn-lg"
                        >Открыть отдел продаж</a>
                        <a
                            :href="selectedAccount.account_url"
                            :class="['btn', 'btn-lg', moduleShop && selectedShop ? 'btn-outline-secondary' : 'btn-success']"
                        >Перейти в бизнес-аккаунт</a>
                        <button type="button" class="btn btn-outline-secondary btn-lg" v-on:click="startAnotherSetup">Настроить ещё</button>
                    </div>
                    <p class="seller-onboarding__ready-note">Переход произойдёт только после нажатия кнопки.</p>
                </div>
            </form>
        </template>
    </div>
</template>

<script>
import FieldErrors from '@/storefront/FieldErrors.vue';
import {createSellerOnboardingApi} from '@/storefront/sellerOnboardingApi';
import {sellerOnboardingComputed, sellerOnboardingStateMethods} from '@/storefront/sellerOnboardingState';
import {sellerOnboardingWorkflowMethods} from '@/storefront/sellerOnboardingWorkflow';

export default {
    name: 'SellerOnboarding',
    components: {FieldErrors},
    props: {
        config: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            onboardingApi: null,
            loading: true,
            sending: false,
            step: 'profile',
            state: {
                authenticated: false,
                user: null,
                accounts: [],
            },
            profileMode: 'login',
            profileForm: {
                name: '', email: '', password: '', password_confirmation: '',
            },
            selectedAccountId: null,
            createNewAccount: true,
            accountForm: {
                name: '', legal_form: 'ИП', inn: '', phone: '', email: '',
            },
            moduleShop: false,
            moduleSeller: false,
            moduleDelivery: false,
            selectedShopId: null,
            selectedSellerId: null,
            createNewShop: true,
            shopForm: {name: ''},
            errors: {},
            generalError: null,
        };
    },
    computed: sellerOnboardingComputed,
    created() {
        this.onboardingApi = createSellerOnboardingApi(this.config);
        this.restoreDraft();
        this.loadState(true);
    },
    methods: {
        ...sellerOnboardingStateMethods,
        ...sellerOnboardingWorkflowMethods,
    },
};
</script>
